import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@/lib/api";

const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const CACHE_KEY = "onboard.vehicleOptions.v1";
let inMemoryCache = null;

function uniqueSorted(values) {
  return [...new Set(
    (Array.isArray(values) ? values : [])
      .map((v) => String(v ?? "").trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));
}

function normalizeOptions(payload) {
  const root = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const makes = uniqueSorted(root?.makes);
  const models = uniqueSorted(root?.models);
  const vehicleTypes = uniqueSorted(root?.vehicle_types || root?.vehicleTypes);
  return { makes, models, vehicleTypes };
}

function readSessionCache() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      !Array.isArray(parsed.makes) ||
      !Array.isArray(parsed.models) ||
      !Array.isArray(parsed.vehicleTypes)
    ) {
      return null;
    }
    return {
      makes: uniqueSorted(parsed.makes),
      models: uniqueSorted(parsed.models),
      vehicleTypes: uniqueSorted(parsed.vehicleTypes),
    };
  } catch {
    return null;
  }
}

function writeSessionCache(value) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // ignore storage quota/privacy errors
  }
}

export function useOnboardVehicleOptions({ enabled = true } = {}) {
  const initialCache = inMemoryCache || readSessionCache() || { makes: [], models: [], vehicleTypes: [] };
  const [options, setOptions] = useState(initialCache);
  const [loading, setLoading] = useState(
    enabled &&
      initialCache.makes.length === 0 &&
      initialCache.models.length === 0 &&
      initialCache.vehicleTypes.length === 0
  );

  useEffect(() => {
    if (!enabled) return undefined;

    let mounted = true;

    const load = async () => {
      if (inMemoryCache) {
        setOptions(inMemoryCache);
        setLoading(false);
        return;
      }

      const cached = readSessionCache();
      if (cached) {
        inMemoryCache = cached;
        setOptions(cached);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await publicApi.get("/onboard/vehicle-options");
        if (!mounted) return;

        const normalized = normalizeOptions(response?.data);
        inMemoryCache = normalized;
        writeSessionCache(normalized);
        setOptions(normalized);
      } catch {
        if (!mounted) return;
        setOptions({ makes: [], models: [], vehicleTypes: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [enabled]);

  return { ...options, loading };
}
